/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * CHON bond-stretch stiffness pack for the "MMFF-lite" upgrade to the default
 * molecular force field. Keyed by an unordered pair of atom types (from
 * mmffLiteTyping) plus the integer bond order.
 *
 * UNITS. RadialBond.compute() evaluates V = ½·strength·(r − r₀)² with r in Å
 * and V in eV, so `strength` is in eV/Å². Spectroscopic / MMFF force constants
 * are tabulated in mdyn/Å; the conversion is
 *
 *     k[eV/Å²] = k[mdyn/Å] · 6.2415          (1 mdyn/Å = 100 N/m = 6.2415 eV/Å²)
 *
 * The mdyn/Å value is shown in each comment so the table can be cross-checked
 * against a spreadsheet or a published force field. As a sanity anchor, the
 * harmonic-oscillator wavenumber of a diatomic is
 *
 *     ν̃[cm⁻¹] = 1303.16 · √( k[mdyn/Å] / μ[amu] )
 *
 * which the verifier (mmffLite.test.ts) uses to show these constants reproduce
 * the right stretch bands, where the global default (k = 10 eV/Å² ≈ 1.6 mdyn/Å)
 * does not.
 *
 * REGIME A. Only the stiffness is imported here; the equilibrium length r₀ is
 * left at the as-built bond length (RadialBond keeps `length` from the loaded
 * geometry). So the structure stays at its own minimum (V = 0 at rest) and
 * there is no rest-energy injection — the failure mode that forced the torsion
 * pack (torsionParameters.ts) to ship disabled. Importing reference r₀ as well
 * (Regime B) would need a pre-MD minimization pass.
 *
 * Values are trend-correct CHON stand-ins drawn from standard spectroscopic
 * force constants and MMFF94 bond-stretch parameters. Add entries only for
 * type pairs cross-checked against a reference; anything absent falls back to
 * the engine default (see mmffParamLookup). This is a CHON-scoped pack, NOT a
 * full MMFF replacement.
 */

const MDYN_TO_EV_PER_A2 = 6.2415;
const k = (mdynPerA: number): number => mdynPerA * MDYN_TO_EV_PER_A2;

// Canonical key: the two type tags sorted, joined with '~', then the bond
// order. 'C.3~H|1' etc. Sorting makes the lookup order-independent.
const bondKey = (typeA: string, typeB: string, order: number): string => {
  const [a, b] = typeA <= typeB ? [typeA, typeB] : [typeB, typeA];
  return `${a}~${b}|${order}`;
};

// eV/Å². The mdyn/Å source value and the diatomic band it implies are noted.
export const MMFF_LITE_BOND_PARAMETERS: Readonly<Record<string, number>> = {
  // ---- C–H stretches (~2900–3300 cm⁻¹) ----
  [bondKey('C.3', 'H', 1)]: k(4.8), // sp³ C–H   ~2970 cm⁻¹
  [bondKey('C.2', 'H', 1)]: k(5.1), // sp² C–H   ~3060 cm⁻¹
  [bondKey('C.ar', 'H', 1)]: k(5.1), // aromatic C–H ~3060 cm⁻¹
  [bondKey('C.1', 'H', 1)]: k(5.9), // sp  ≡C–H  ~3300 cm⁻¹

  // ---- X–H stretches ----
  [bondKey('O.3', 'H', 1)]: k(7.6), // O–H       ~3700 cm⁻¹
  [bondKey('N.3', 'H', 1)]: k(6.5), // N–H amine ~3400 cm⁻¹
  [bondKey('N.2', 'H', 1)]: k(6.6), // N–H imine ~3400 cm⁻¹

  // ---- C–C framework ----
  [bondKey('C.3', 'C.3', 1)]: k(4.5), // C–C sp³        ~1000 cm⁻¹
  [bondKey('C.3', 'C.2', 1)]: k(4.6),
  [bondKey('C.2', 'C.2', 1)]: k(4.8), // conjugated single
  [bondKey('C.2', 'C.2', 2)]: k(9.6), // C=C            ~1640 cm⁻¹
  [bondKey('C.1', 'C.1', 3)]: k(15.6), // C≡C           ~2100 cm⁻¹
  // Aromatic ring C–C: give both Kekulé orders the same delocalized stiffness
  // so the ring is uniform regardless of how single/double alternate.
  [bondKey('C.ar', 'C.ar', 1)]: k(7.8), // ~1500–1600 cm⁻¹
  [bondKey('C.ar', 'C.ar', 2)]: k(7.8),

  // ---- C–O ----
  [bondKey('C.3', 'O.3', 1)]: k(5.4), // C–O ether/alcohol ~1050 cm⁻¹
  [bondKey('C.2', 'O.3', 1)]: k(5.7), // ester/acid C–O
  [bondKey('C.2', 'O.2', 2)]: k(12.3), // C=O carbonyl    ~1730 cm⁻¹
  // Cumulated C=O on an sp carbon (O=C=O in CO₂, also ketene/isocyanate centers).
  // Much stiffer than a carbonyl: tuned so the IR-active asymmetric stretch ν₃ of
  // CO₂ lands at ~2349 cm⁻¹ (ω₃² = k·(1/m_O + 2/m_C); see co2Spectrum.test.ts).
  // The model has no stretch–stretch interaction term, so the symmetric stretch
  // ν₁ comes out low (~1230) — but ν₁ is IR-INACTIVE, so it never shows.
  [bondKey('C.1', 'O.2', 2)]: k(14.19), // O=C=O cumulated C=O ~2349 cm⁻¹ (ν₃)

  // ---- C–N ----
  [bondKey('C.3', 'N.3', 1)]: k(5.1), // C–N amine  ~1030 cm⁻¹
  [bondKey('C.2', 'N.2', 2)]: k(10.2), // C=N imine ~1640 cm⁻¹
  [bondKey('C.1', 'N.1', 3)]: k(19.5), // C≡N nitrile ~2250 cm⁻¹

  // ---- O–O / N–N / O=O ----
  [bondKey('O.3', 'O.3', 1)]: k(3.8), // peroxide O–O
  [bondKey('N.3', 'N.3', 1)]: k(3.5), // hydrazine N–N
  [bondKey('N.2', 'N.2', 2)]: k(9.0), // N=N
  [bondKey('O.2', 'O.2', 2)]: k(11.4), // O=O          ~1580 cm⁻¹
};

/**
 * Look up the bond-stretch stiffness (eV/Å²) for a typed pair at a given bond
 * order. Returns null when the pair is not in the pack — the caller then leaves
 * RadialBond.DEFAULT_STRENGTH in place.
 */
export const lookupBondStrength = (typeA: string, typeB: string, order: number): number | null => {
  const v = MMFF_LITE_BOND_PARAMETERS[bondKey(typeA, typeB, order)];
  return v === undefined ? null : v;
};
